/* global globalThis */
/**
 * Optional, incremental YouTube authorization.
 *
 * SECURITY BOUNDARY
 * -----------------
 * The OAuth access token lives in this module's memory for the lifetime of the
 * tab and nowhere else. It is never written to Firestore, localStorage,
 * sessionStorage, an event document or a user document, so it can never be
 * exposed to party guests. Closing the tab drops it and the host re-authorizes.
 *
 * The read-only scope is requested ONLY here. Basic "Continue with Google"
 * sign-in in services/authProviders.js asks for profile scopes only.
 */
import { GoogleAuthProvider, reauthenticateWithPopup } from 'firebase/auth';
import { createGoogleProvider, getCurrentUser } from './authProviders';
import { classifyAuthError, hasGoogleProvider } from '../utils/authIdentity';

export const YOUTUBE_READONLY_SCOPE = 'https://www.googleapis.com/auth/youtube.readonly';

/** Google access tokens last ~1h; expire early to avoid mid-request failures. */
const ASSUMED_TOKEN_LIFETIME_MS = 55 * 60 * 1000;

let accessToken = null;
let accessTokenExpiresAt = 0;
let authorizedUid = null;

export function clearYouTubeAuthorization() {
  accessToken = null;
  accessTokenExpiresAt = 0;
  authorizedUid = null;
}

export function getYouTubeAccessToken({ now = Date.now() } = {}) {
  if (!accessToken) return null;
  if (now >= accessTokenExpiresAt) {
    clearYouTubeAuthorization();
    return null;
  }
  if (authorizedUid && getCurrentUser()?.uid && authorizedUid !== getCurrentUser().uid) {
    clearYouTubeAuthorization();
    return null;
  }
  return accessToken;
}

export function hasYouTubeAuthorization(options = {}) {
  return Boolean(getYouTubeAccessToken(options));
}

export async function revokeYouTubeAccess({
  fetchImpl = globalThis.fetch,
} = {}) {
  const token = accessToken;
  if (!token) {
    clearYouTubeAuthorization();
    return { ok: true, revoked: false, reason: 'no-token' };
  }

  // Expired in-memory tokens are still sent to Google's revoke endpoint.
  // Local state is always cleared, even when the network revoke fails.
  try {
    const fetchFn = typeof fetchImpl === 'function' ? fetchImpl : globalThis.fetch;
    if (typeof fetchFn !== 'function') {
      clearYouTubeAuthorization();
      return { ok: false, revoked: false, reason: 'revoke-unavailable' };
    }
    const response = await fetchFn('https://oauth2.googleapis.com/revoke', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: `token=${encodeURIComponent(token)}`,
    });
    clearYouTubeAuthorization();
    if (response && response.ok === false) {
      return { ok: false, revoked: false, reason: 'revoke-failed', status: response.status };
    }
    return { ok: true, revoked: true };
  } catch (error) {
    clearYouTubeAuthorization();
    return { ok: false, revoked: false, reason: 'revoke-failed', error };
  }
}

export function setYouTubeAccessTokenForTests(token, { uid = null, lifetimeMs = ASSUMED_TOKEN_LIFETIME_MS } = {}) {
  accessToken = token;
  accessTokenExpiresAt = token ? Date.now() + lifetimeMs : 0;
  authorizedUid = uid;
}

/**
 * Ask Google for read-only YouTube access.
 *
 * Requires an already Google-linked account: authorization is an upgrade on top
 * of sign-in, never a substitute for it.
 */
export async function authorizeYouTube() {
  const user = getCurrentUser();

  if (!user) {
    return { ok: false, reason: 'not-signed-in' };
  }

  if (!hasGoogleProvider(user)) {
    return { ok: false, reason: 'google-required' };
  }

  const existing = getYouTubeAccessToken();
  if (existing) {
    return { ok: true, reason: 'already-authorized' };
  }

  // No prompt override. The first authorization shows Google's consent screen
  // anyway; forcing prompt=consent on every reconnect only meant a returning
  // host re-approved YouTube access each tab. Google still decides for itself
  // when consent or account selection is genuinely required.
  const provider = createGoogleProvider({
    scopes: [YOUTUBE_READONLY_SCOPE],
  });

  try {
    const result = await reauthenticateWithPopup(user, provider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    const token = credential?.accessToken || null;

    if (!token) {
      return { ok: false, reason: 'no-token' };
    }

    accessToken = token;
    accessTokenExpiresAt = Date.now() + ASSUMED_TOKEN_LIFETIME_MS;
    authorizedUid = result?.user?.uid || user.uid;

    return { ok: true, reason: 'authorized' };
  } catch (error) {
    const classified = classifyAuthError(error);
    return {
      ok: false,
      reason: classified.kind === 'cancelled' ? 'cancelled' : 'failed',
      error: classified,
    };
  }
}
